import React, { useEffect, useState } from 'react';

// Detect iOS (Safari, Chrome-on-iOS, etc.) — no beforeinstallprompt support
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

// Detect standalone mode (already installed)
const isStandalone = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true;

interface PWAInstallBannerProps {
    /** Show the banner — true when ?install=1 is in the URL */
    show: boolean;
    /** Called when the user dismisses or completes the install */
    onDismiss: () => void;
}

export default function PWAInstallBanner({ show, onDismiss }: PWAInstallBannerProps) {
    const [promptAvailable, setPromptAvailable] = useState(
        !!(window as any).__pwaInstallPrompt
    );
    const [installed, setInstalled] = useState(false);

    // Listen for the prompt becoming available after mount
    useEffect(() => {
        const onAvailable = () => setPromptAvailable(true);
        window.addEventListener('pwaInstallAvailable', onAvailable);
        return () => window.removeEventListener('pwaInstallAvailable', onAvailable);
    }, []);

    // Listen for successful installation
    useEffect(() => {
        const onInstalled = () => {
            setInstalled(true);
            setTimeout(() => {
                onDismiss();
                window.location.href = '/';
            }, 1800);
        };
        window.addEventListener('appinstalled', onInstalled);
        return () => window.removeEventListener('appinstalled', onInstalled);
    }, [onDismiss]);

    if (!show || isStandalone()) return null;

    const handleInstall = async () => {
        const prompt = (window as any).__pwaInstallPrompt;
        if (!prompt) return;
        await prompt.prompt();
        const { outcome } = await prompt.userChoice;
        (window as any).__pwaInstallPrompt = null;
        if (outcome === 'accepted') {
            setInstalled(true);
        } else {
            onDismiss();
        }
    };

    const overlayStyle: React.CSSProperties = {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 10500,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: '0 0 24px',
    };

    const cardStyle: React.CSSProperties = {
        background: '#111',
        borderRadius: 20,
        width: 'min(440px, calc(100vw - 32px))',
        padding: '28px 24px 24px',
        boxShadow: '0 -4px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,204,0,0.2)',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    };

    if (installed) {
        return (
            <div style={overlayStyle}>
                <div style={cardStyle}>
                    <div style={{ textAlign: 'center', fontSize: '2.5rem' }}>🎉</div>
                    <p style={{ margin: 0, textAlign: 'center', color: '#fff', fontWeight: 700, fontSize: '1rem' }}>
                        sConscious installed!
                    </p>
                    <p style={{ margin: 0, textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem' }}>
                        You can now open it from your home screen.
                    </p>
                </div>
            </div>
        );
    }

    // ── iOS: show manual Add-to-Home-Screen instructions ──
    if (isIos()) {
        return (
            <div style={overlayStyle} onClick={onDismiss}>
                <div style={cardStyle} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 800, fontSize: '1rem', color: '#fff' }}>Add to Home Screen</span>
                        <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '1.4rem', cursor: 'pointer', padding: 0 }}>×</button>
                    </div>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.84rem', lineHeight: 1.6 }}>
                        To install sConscious on your iPhone or iPad:
                    </p>
                    <ol style={{ margin: 0, padding: '0 0 0 20px', color: 'rgba(255,255,255,0.85)', fontSize: '0.84rem', lineHeight: 2 }}>
                        <li>Tap the <strong style={{ color: '#ffcc00' }}>Share</strong> button at the bottom of Safari <span style={{ fontSize: '1.1em' }}>⬆</span></li>
                        <li>Scroll down and tap <strong style={{ color: '#ffcc00' }}>Add to Home Screen</strong></li>
                        <li>Tap <strong style={{ color: '#ffcc00' }}>Add</strong></li>
                    </ol>
                    <button
                        onClick={onDismiss}
                        style={{
                            background: 'rgba(255,255,255,0.1)',
                            border: 'none',
                            borderRadius: 12,
                            color: 'rgba(255,255,255,0.6)',
                            padding: '12px 0',
                            fontWeight: 700,
                            fontSize: '0.88rem',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            marginTop: 4,
                        }}
                    >
                        Got it
                    </button>
                    {/* iOS-specific pointing arrow */}
                    <div style={{
                        position: 'absolute',
                        bottom: -1,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0, height: 0,
                        borderLeft: '10px solid transparent',
                        borderRight: '10px solid transparent',
                        borderTop: '12px solid #111',
                    }} />
                </div>
            </div>
        );
    }

    // ── Android / Chrome / Edge: native install prompt ──
    return (
        <div style={overlayStyle} onClick={onDismiss}>
            <div style={cardStyle} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 800, fontSize: '1rem', color: '#fff' }}>Install sConscious</span>
                    <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '1.4rem', cursor: 'pointer', padding: 0 }}>×</button>
                </div>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.65)', fontSize: '0.84rem', lineHeight: 1.6 }}>
                    Add sConscious to your home screen for an app-like experience — works offline, no app store needed.
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                    {promptAvailable ? (
                        <button
                            onClick={handleInstall}
                            style={{
                                flex: 2,
                                background: '#ffcc00',
                                color: '#111',
                                border: 'none',
                                borderRadius: 12,
                                padding: '13px 0',
                                fontWeight: 800,
                                fontSize: '0.88rem',
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                            }}
                        >
                            Install
                        </button>
                    ) : (
                        <button
                            disabled
                            style={{
                                flex: 2,
                                background: 'rgba(255,204,0,0.3)',
                                color: 'rgba(17,17,17,0.5)',
                                border: 'none',
                                borderRadius: 12,
                                padding: '13px 0',
                                fontWeight: 800,
                                fontSize: '0.88rem',
                                cursor: 'default',
                                fontFamily: 'inherit',
                            }}
                        >
                            Not available in this browser
                        </button>
                    )}
                    <button
                        onClick={onDismiss}
                        style={{
                            flex: 1,
                            background: 'rgba(255,255,255,0.08)',
                            color: 'rgba(255,255,255,0.7)',
                            border: 'none',
                            borderRadius: 12,
                            padding: '13px 0',
                            fontWeight: 700,
                            fontSize: '0.88rem',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                        }}
                    >
                        Later
                    </button>
                </div>
            </div>
        </div>
    );
}
